import type { FC, ReactElement } from "react";
import { useEffect, useMemo, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import { createGlobalStyle } from "styled-components";

import { colorsPalettes } from "@similarweb/styles";
import { Badge, Dropdown, DropdownButton, LockDropdownItem, SimpleDropdownItem } from "@similarweb/ui-components";
import { Box } from "@similarweb/ui-components/foundations";

import { swSettings } from "app/common/services/swSettings";
import { onHookClick } from "app/components/sidebar3/SideBarItems";
import { translate } from "app/filters/i18nFilter";
import { DROPDOWN_MODELS } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/components/model-dropdown/consts";
import { ModelItem } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/components/model-dropdown/ModelItem";
import { useLLMTabs } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/hooks/useLLMTabs";
import {
    filterOpenModelsForUser,
    getDefaultModel,
    isAllPlatformsOptionEnabled,
    navigateToReport,
    sortModels,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMTrackerUtils";
import type {
    ExplicitLLModel,
    IAiTrackerCampaign,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { CampaignStatus, LLModel } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { EHooksVersions } from "app/services/SwHook";
import { allTrackers } from "app/services/track/track";
import { isNoTouch } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";

const ModelDropdownPopupStyles = createGlobalStyle`
    .llm-model-dropdown-container .DropdownItem .item-badge {
        text-indent: 0;
    }
`;

interface IModelDropdownProps {
    selectedCampaign: IAiTrackerCampaign;
    isDisabled: boolean;
}

export const ModelDropdown: FC<IModelDropdownProps> = ({ selectedCampaign, isDisabled }) => {
    const {
        params: { model: queryModel },
    } = useCurrentStateAndParams();

    const { currentTab } = useLLMTabs();
    const [selectedModel, setSelectedModel] = useState<LLModel>(queryModel as LLModel);
    const defaultModel = useMemo<LLModel>(
        () => getDefaultModel(selectedCampaign, currentTab.id),
        [selectedCampaign, currentTab],
    );

    const promoModels = Object.entries(DROPDOWN_MODELS)
        .filter(([, config]) => config.isPromotion)
        .map(([model]) => model) as LLModel[];

    const supportsAllPlatforms = isAllPlatformsOptionEnabled(currentTab.id);
    const supportedCampaignModels = useMemo<LLModel[]>(() => {
        return Object.entries(selectedCampaign.configs)
            .filter(([model, value]) => {
                const config = DROPDOWN_MODELS?.[model as LLModel];
                const isPending = value.status === CampaignStatus.Pending;

                return (
                    !config?.isPromotion &&
                    !config?.isNotSupported &&
                    (isPending || (value.firstScrapeDate && value.lastScrapeDate))
                );
            })
            .map(([model]) => model) as LLModel[];
    }, [selectedCampaign]);

    const availableModels = useMemo<LLModel[]>(() => {
        const openModels = filterOpenModelsForUser(supportedCampaignModels, selectedCampaign.isDemo);
        return openModels?.length
            ? sortModels(
                  openModels.filter((model) => supportsAllPlatforms || model !== LLModel.All),
                  defaultModel,
              )
            : [defaultModel];
    }, [selectedCampaign, defaultModel, supportedCampaignModels, supportsAllPlatforms]);

    const hookedModels = useMemo<LLModel[]>(() => {
        const isNTUser = isNoTouch(swSettings);

        //show all as locked for NT users
        if (isNTUser) {
            return Object.entries(DROPDOWN_MODELS)
                .filter(
                    ([model, config]) =>
                        (model as LLModel) !== LLModel.All &&
                        !config.isNotSupported &&
                        !availableModels.includes(model as LLModel),
                )
                .map(([model]) => model) as LLModel[];
        }

        return supportedCampaignModels.filter((model) => !availableModels.includes(model) && model !== LLModel.All);
    }, [supportedCampaignModels, availableModels]);

    const onModelSelection = (model: LLModel): void => {
        if (model === selectedModel) {
            return;
        }

        setSelectedModel(model);
        navigateToReport(selectedCampaign, model, currentTab.id);
        allTrackers.dispatchTrackEvent("Drop Down", "click", `LLM Change/${model}`);
    };

    const openHook = (): void => {
        onHookClick("AiTracker", "digitalsuite_llm_solutions_homepage", EHooksVersions.V5);
    };

    const getModelBadge = (model: LLModel): ReactElement<typeof Badge> | undefined => {
        if (model === LLModel.All) {
            return;
        }

        const status = selectedCampaign?.configs?.[model]?.status;
        if (!status) {
            return;
        }

        switch (status) {
            case CampaignStatus.Pending:
                return (
                    <Badge
                        type="custom"
                        bgColor={colorsPalettes.teal[400]}
                    >
                        {translate("llm.model_selector.badge.pending")}
                    </Badge>
                );

            case CampaignStatus.Failed:
                return (
                    <Badge
                        type="custom"
                        bgColor={colorsPalettes.red[400]}
                    >
                        {translate("llm.model_selector.badge.failed")}
                    </Badge>
                );
        }

        return;
    };

    //align with the model in the URL, and fix it if invalid for the current campaign
    useEffect(() => {
        if (queryModel && queryModel !== selectedModel) {
            if (queryModel in selectedCampaign.configs) {
                setSelectedModel(queryModel as LLModel);
            } else {
                navigateToReport(selectedCampaign, queryModel as LLModel, currentTab.id);
            }
        }
    }, [queryModel]);

    return (
        <>
            <ModelDropdownPopupStyles />
            <Dropdown
                appendTo="body"
                closeOnItemClick
                shouldScrollToSelected
                width={200}
                buttonWidth={180}
                onClick={({ id }) => onModelSelection(id as LLModel)}
                disabled={!selectedCampaign || isDisabled}
                dropdownPopupPlacement={"bottom-left"}
                cssClassContainer="DropdownContent-container FiltersBarDropdown llm-filter-dropdown llm-model-dropdown-container"
            >
                {[
                    <DropdownButton
                        key="llm-model-dropdown-button"
                        cssClass="DropdownButton--filtersBarDropdownButton"
                        disabled={!selectedCampaign || isDisabled}
                        height={60}
                    >
                        <Box width="180px">
                            <ModelItem
                                model={selectedModel}
                                paddingLeft="0"
                            />
                        </Box>
                    </DropdownButton>,
                    ...availableModels.map((model) => (
                        <SimpleDropdownItem
                            key={model}
                            id={model}
                        >
                            <ModelItem
                                model={model}
                                badge={getModelBadge(model)}
                            />
                        </SimpleDropdownItem>
                    )),
                    ...hookedModels.map((model) => (
                        <LockDropdownItem
                            key={model}
                            id={model}
                            preventDefault
                            onClick={openHook}
                        >
                            <ModelItem model={model} />
                        </LockDropdownItem>
                    )),
                    ...promoModels.map((model) => (
                        <SimpleDropdownItem
                            key={model}
                            id={model}
                            disabled
                        >
                            <ModelItem
                                model={model}
                                badge={<Badge type="coming soon" />}
                            />
                        </SimpleDropdownItem>
                    )),
                ]}
            </Dropdown>
        </>
    );
};
